// shared-components/components/Card.jsx
import React from 'react';

/**
 * Unified Card Component
 * 
 * Provides consistent card styling with multiple variants and interactive states.
 * All styles use design tokens for maintainability and consistency.
 * 
 * Variants:
 * - default: Standard card with border and subtle shadow
 * - elevated: Card with prominent shadow, no border
 * - flat: No shadow, just border
 * - outlined: Prominent border, no shadow
 * - interactive: Hover state for clickable cards
 */

/**
 * Card - Unified card component for all card patterns
 * 
 * @param {React.ReactNode} children - Card content
 * @param {string} variant - Card style variant: 'default', 'elevated', 'flat', 'outlined', 'interactive'
 * @param {string} size - Padding size: 'small', 'medium', 'large'
 * @param {Function} onClick - Optional click handler (makes card interactive)
 * @param {string} className - Additional CSS classes
 * @param {object} style - Additional inline styles
 */
export function Card({ 
  children, 
  variant = 'default', 
  size = 'medium',
  onClick,
  className,
  style,
  ...props 
}) {
  const sizeStyles = {
    small: {
      padding: 'var(--space-4)'
    },
    medium: {
      padding: 'var(--space-6)'
    },
    large: {
      padding: 'var(--space-8)'
    }
  };

  const variantStyles = {
    default: {
      background: 'var(--bg-primary)',
      border: '1px solid var(--border-default)',
      boxShadow: 'var(--shadow-sm)'
    },
    elevated: {
      background: 'var(--bg-primary)',
      border: 'none',
      boxShadow: 'var(--shadow-md)'
    },
    flat: {
      background: 'var(--bg-primary)',
      border: '1px solid var(--border-default)',
      boxShadow: 'none'
    },
    outlined: {
      background: 'var(--bg-primary)',
      border: '2px solid var(--border-default)',
      boxShadow: 'none'
    },
    interactive: {
      background: 'var(--bg-primary)',
      border: '1px solid var(--border-default)',
      boxShadow: 'var(--shadow-sm)',
      cursor: 'pointer'
    }
  };

  const baseStyles = {
    ...sizeStyles[size],
    ...variantStyles[variant],
    borderRadius: 'var(--radius-lg)',
    transition: 'all var(--duration-normal) var(--ease-out)',
    ...style
  };

  const handleMouseEnter = (e) => {
    if (onClick || variant === 'interactive') {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = 'var(--shadow-md)';
    }
  };

  const handleMouseLeave = (e) => {
    if (onClick || variant === 'interactive') {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = variantStyles[variant].boxShadow || 'none';
    }
  };

  return (
    <div
      onClick={onClick}
      className={className}
      style={baseStyles}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * CardHeader - Optional card header with consistent styling
 * 
 * @param {React.ReactNode} children - Header content
 * @param {React.ReactNode} title - Header title (alternative to children)
 * @param {React.ReactNode} subtitle - Optional subtitle
 * @param {React.ReactNode} action - Optional action element (e.g., button)
 */
export function CardHeader({ children, title, subtitle, action, style }) {
  if (children) {
    return (
      <div style={{
        marginBottom: 'var(--space-4)',
        paddingBottom: 'var(--space-4)',
        borderBottom: '1px solid var(--border-subtle)',
        ...style
      }}>
        {children}
      </div>
    );
  }

  return (
    <div style={{
      marginBottom: 'var(--space-4)',
      paddingBottom: 'var(--space-4)',
      borderBottom: '1px solid var(--border-subtle)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      ...style
    }}>
      <div>
        {title && (
          <h3 style={{
            fontSize: 'var(--text-lg)',
            fontWeight: 'var(--font-semibold)',
            color: 'var(--text-primary)',
            margin: 0,
            marginBottom: subtitle ? 'var(--space-1)' : 0
          }}>
            {title}
          </h3>
        )}
        {subtitle && (
          <p style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--text-tertiary)',
            margin: 0
          }}>
            {subtitle}
          </p>
        )}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

/**
 * CardBody - Optional card body with consistent spacing
 * 
 * @param {React.ReactNode} children - Body content
 */
export function CardBody({ children, style }) {
  return (
    <div style={{
      fontSize: 'var(--text-base)',
      color: 'var(--text-secondary)',
      lineHeight: 'var(--leading-normal)',
      ...style
    }}>
      {children}
    </div>
  );
}

/**
 * CardFooter - Optional card footer with consistent styling
 * 
 * @param {React.ReactNode} children - Footer content
 */
export function CardFooter({ children, style }) {
  return (
    <div style={{
      marginTop: 'var(--space-4)',
      paddingTop: 'var(--space-4)',
      borderTop: '1px solid var(--border-subtle)',
      ...style
    }}>
      {children}
    </div>
  );
}

// Export default Card for convenience
export default Card;


